import { DatePipe, NgTemplateOutlet } from '@angular/common';
import { AfterViewInit, Component, computed, effect, ElementRef, inject, input, OnDestroy, output, signal, untracked, viewChild } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActorType, HypothesisItem, ReportBlock, StepStatus, WorkflowComment, WorkflowStep } from '#models';
import type {
  Uc1AgentCaseCandidate,
  Uc1AgentCaseEvidence,
} from '../../../../../models/persisted-case.model';
import { normalizeReportBlocks } from '../../mappers/report-block.mapper';
import { parseTooltipHtml } from '../../mappers/tooltip-html.parser';
import { mapUc1AgentCaseEvidence } from '../../mappers/uc1-evidence.mapper';
import { isComparisonHistoryChart } from '../../mappers/graph-block.mapper';
import { DetailSummaryBlocksComponent } from '../detail-summary-blocks/detail-summary-blocks.component';
import { DetailCommentsComponent } from '../detail-comments/detail-comments.component';

type DetailSectionView = 'summary' | 'evidence' | 'engine' | 'recommended' | 'hypothesis' | 'workflow' | 'comments';

interface Uc1EvidenceView {
  id: string;
  record?: Uc1AgentCaseEvidence;
  displayLabel: string;
  blocks: ReportBlock[];
  issue?: string;
}

interface Uc1CandidateView {
  candidate: Uc1AgentCaseCandidate;
  displayRank: number;
  evidence: Uc1EvidenceView[];
}

@Component({
  selector: 'app-detail-steps',
  imports: [DatePipe, NgTemplateOutlet, DetailSummaryBlocksComponent, DetailCommentsComponent],
  templateUrl: './detail-steps.component.html',
  styleUrl: './detail-steps.component.scss',
})
export class DetailStepsComponent implements AfterViewInit, OnDestroy {
  private _sanitizer = inject(DomSanitizer);
  private _host = inject<ElementRef<HTMLElement>>(ElementRef);
  private _scrollContainer: HTMLElement | null = null;
  private _onScroll = () => this._updateActiveViewFromScroll();
  private _pinnedView: DetailSectionView | null = null;
  private _releasePin = () => { this._pinnedView = null; };
  private readonly _userScrollEvents = ['wheel', 'touchmove', 'keydown', 'pointerdown'] as const;

  steps = input<WorkflowStep[]>([]);
  uc1Candidates = input<Uc1AgentCaseCandidate[] | undefined>(undefined);
  uc1Evidence = input<Uc1AgentCaseEvidence[] | undefined>(undefined);
  summaryText = input<string>('');
  recommendedActions = input<string[]>([]);
  sourceLines = input<string[]>([]);
  comments = input<WorkflowComment[]>([]);
  commentsOpen = input<boolean>(false);
  commentDraft = input<string>('');
  commentPosting = input<boolean>(false);
  hasDecision = input<boolean>(false);
  isClosed = input<boolean>(false);
  /** Adds a navigation entry and section for projected `[engineWorkflow]` content. */
  hasEngineWorkflow = input<boolean>(false);
  isProductionDeviation = input<boolean>(false);
  itemId = input<string | null>(null);
  /** A short label beside the workflow tab, shown only when set. */
  workflowTabBadge = input<string | null>(null);
  requestedTab = input<string | null>(null);
  tabSelected = output<string>();

  approve = output<void>();
  reject = output<void>();
  addComment = output<void>();
  toggleComments = output<void>();
  commentDraftChanged = output<string>();
  commentPosted = output<void>();

  private _openSteps = signal<Set<number>>(new Set());
  private _activeView = signal<DetailSectionView | null>(null);

  summarySection = viewChild<ElementRef<HTMLElement>>('summarySection');
  engineSection = viewChild<ElementRef<HTMLElement>>('engineSection');
  recommendedSection = viewChild<ElementRef<HTMLElement>>('recommendedSection');
  hypothesisSection = viewChild<ElementRef<HTMLElement>>('hypothesisSection');
  workflowSection = viewChild<ElementRef<HTMLElement>>('workflowSection');
  commentsSection = viewChild<ElementRef<HTMLElement>>('commentsSection');
  workflowHeader = viewChild<ElementRef<HTMLElement>>('workflowHeader');

  readonly StepStatus = StepStatus;
  activeView = computed<DetailSectionView>(() => this._activeView() ?? (this.isProductionDeviation() ? 'hypothesis' : 'summary'));

  visibleSteps = computed(() => this.steps());
  hasUc1Candidates = computed(() => this.uc1Candidates() !== undefined);
  hasInvestigationSection = computed(() => !this.hasUc1Candidates() || this.visibleSteps().length > 0);
  candidateSectionLabel = computed(() => this.hasUc1Candidates() ? 'candidates' : 'hypotheses');
  sortedUc1Candidates = computed(() => [...(this.uc1Candidates() ?? [])].sort((left, right) => left.rank - right.rank));
  private evidenceById = computed(() => new Map((this.uc1Evidence() ?? []).map(record => [record.evidenceId, record])));
  uc1CandidateViews = computed<Uc1CandidateView[]>(() => this.sortedUc1Candidates().map((candidate, index) => ({
    candidate,
    displayRank: candidate.rank ?? (index + 1),
    evidence: this.toEvidenceViews(candidate.evidenceIds ?? []),
  })));

  constructor() {
    effect(() => {
      this.itemId();
      const tabbed = this.hasEngineWorkflow();
      const requested = this.requestedTab();
      const uc1 = this.isProductionDeviation();
      const firstView: DetailSectionView = uc1 ? 'hypothesis' : 'summary';
      const views: Record<string, DetailSectionView> = {
        summary: firstView, evidence: uc1 ? firstView : 'evidence', workflow: 'engine',
        'recommended-actions': uc1 ? firstView : 'recommended',
        candidates: 'hypothesis', comments: 'comments', investigation: 'workflow',
      };
      const view = requested && Object.hasOwn(views, requested) ? views[requested] : undefined;
      const available = (view !== 'workflow' || this.hasInvestigationSection())
        && (view !== 'evidence' || this.isProductionDeviation());
      const firstViewFallback = uc1 && !tabbed && (!view || view === 'engine' || view === firstView);
      untracked(() => {
        this._activeView.set(tabbed && view && available ? view : null);
        this._pinnedView = firstViewFallback ? firstView : null;
      });
    });
  }

  private toEvidenceViews(ids: string[]): Uc1EvidenceView[] {
    return ids.filter(id => {
      const record = this.evidenceById().get(id);
      return record?.display?.type !== 'timeGraph' || !isComparisonHistoryChart(record.title);
    }).map(id => this.toEvidenceView(id));
  }

  private toEvidenceView(id: string): Uc1EvidenceView {
    const record = this.evidenceById().get(id);
    if (!record) {
      return {
        id,
        displayLabel: 'Missing record',
        blocks: [],
        issue: 'This evidence reference is not present in the case payload.',
      };
    }

    return {
      id,
      record,
      displayLabel: record.display?.type === 'timeGraph'
        ? 'Time graph'
        : record.display?.type === 'table'
          ? 'Table'
          : 'Unsupported evidence',
      ...mapUc1AgentCaseEvidence(record),
    };
  }

  // Shows the last hypotheses block found across the steps; within it a duplicate needs the title AND body to match.
  hypotheses = computed<HypothesisItem[]>(() => {
    let lastBlockItems: HypothesisItem[] = [];
    for (const step of this.steps()) {
      const blocks: ReportBlock[] = [
        ...(step.blocks ?? []),
        ...(normalizeReportBlocks(step.evidenceDataJson) ?? []),
      ];
      for (const block of blocks) {
        if (block.type === 'hypotheses') {
          lastBlockItems = block.items;
        }
      }
    }

    const byKey = new Map<string, HypothesisItem>();
    for (const item of lastBlockItems) {
      const key = `${item.title.trim().toLowerCase()}\u0000${item.body.trim().toLowerCase()}`;
      byKey.set(key, item);
    }
    return [...byKey.values()].sort((left, right) => left.rank - right.rank);
  });

  ngAfterViewInit(): void {
    this._initializeScrollSpy();
    this._updateActiveViewFromScroll();
  }

  ngOnDestroy(): void {
    if (this._scrollContainer) {
      this._scrollContainer.removeEventListener('scroll', this._onScroll);
      this._userScrollEvents.forEach(name => this._scrollContainer?.removeEventListener(name, this._releasePin));
      this._scrollContainer = null;
    }
  }

  scrollToView(view: DetailSectionView): void {
    if (this.isProductionDeviation() && (view === 'summary' || view === 'recommended' || view === 'evidence')) {
      view = 'hypothesis';
    }
    if (this.hasEngineWorkflow()) {
      this._activeView.set(view);
      const tabs: Record<DetailSectionView, string> = {
        summary: 'summary', evidence: 'evidence', engine: 'workflow', recommended: 'recommended-actions',
        hypothesis: 'candidates', workflow: 'investigation', comments: 'comments',
      };
      this.tabSelected.emit(tabs[view]);
      return;
    }

    // Pinned so the scroll spy cannot override a section that is too short to reach the top.
    this._pinnedView = view;
    this._activeView.set(view);

    const target = view === 'summary'
      ? this.summarySection()?.nativeElement
      : view === 'engine'
        ? this.engineSection()?.nativeElement
        : view === 'recommended'
          ? this.recommendedSection()?.nativeElement
          : view === 'hypothesis'
            ? this.hypothesisSection()?.nativeElement
            : view === 'workflow'
              ? this.workflowSection()?.nativeElement
              : this.commentsSection()?.nativeElement;

    target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  isStepOpen(stepNumber: number): boolean {
    return this._openSteps().has(stepNumber);
  }

  toggleStep(stepNumber: number): void {
    this._openSteps.update(open => {
      const next = new Set(open);
      if (next.has(stepNumber)) {
        next.delete(stepNumber);
      } else {
        next.add(stepNumber);
      }
      return next;
    });
  }

  parseEvidenceBlocks(json: string | null | undefined) {
    return normalizeReportBlocks(json);
  }

  safeHtml(raw: string): SafeHtml {
    return this._sanitizer.bypassSecurityTrustHtml(parseTooltipHtml(raw));
  }

  getStepDotStatusClass(status: StepStatus): string {
    const map: Record<StepStatus, string> = {
      [StepStatus.Done]: 'step-dot step-dot--done',
      [StepStatus.Current]: 'step-dot step-dot--current',
      [StepStatus.Pending]: 'step-dot step-dot--pending',
      [StepStatus.Waiting]: 'step-dot step-dot--waiting',
    };
    return map[status] ?? 'step-dot step-dot--pending';
  }

  getActorClass(actor: ActorType): string {
    const map: Record<ActorType, string> = {
      [ActorType.System]: 'actor-tag actor-tag--system',
      [ActorType.AIAgent]: 'actor-tag actor-tag--ai',
      [ActorType.Human]: 'actor-tag actor-tag--human',
    };
    return map[actor] ?? 'actor-tag';
  }

  getActorLabel(actor: ActorType): string {
    const map: Record<ActorType, string> = {
      [ActorType.System]: 'System',
      [ActorType.AIAgent]: 'AI Agent',
      [ActorType.Human]: 'Human',
    };
    return map[actor];
  }

  getStepTitle(step: WorkflowStep): string {
    const normalizedTitle = step.title.replace(/^step\s*\d+\s*[-\u2013\u2014]\s*/i, '').trim();
    return `Step ${step.number} - ${normalizedTitle || step.title}`;
  }

  getCollapsedSummary(step: WorkflowStep): string {
    const text = step.summary?.trim() || step.sub?.trim() || '';
    return text.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  }

  private _initializeScrollSpy(): void {
    const host = this._host.nativeElement;
    this._scrollContainer = host.closest('.detail-content') as HTMLElement | null;
    this._scrollContainer?.addEventListener('scroll', this._onScroll, { passive: true });
    this._userScrollEvents.forEach(name =>
      this._scrollContainer?.addEventListener(name, this._releasePin, { passive: true }));
  }

  private _getSections(): { view: DetailSectionView; element: HTMLElement }[] {
    const sections = [
      { view: 'summary' as const, element: this.summarySection()?.nativeElement },
      { view: 'engine' as const, element: this.engineSection()?.nativeElement },
      { view: 'recommended' as const, element: this.recommendedSection()?.nativeElement },
      { view: 'hypothesis' as const, element: this.hypothesisSection()?.nativeElement },
      { view: 'comments' as const, element: this.commentsSection()?.nativeElement },
      { view: 'workflow' as const, element: this.workflowSection()?.nativeElement },
    ].filter((section): section is typeof section & { element: HTMLElement } => !!section.element);

    return sections;
  }

  private _updateActiveViewFromScroll(): void {
    const sections = this._getSections();
    if (sections.length === 0 || this._pinnedView || this.hasEngineWorkflow()) {
      return;
    }

    if (this._scrollContainer) {
      const { scrollTop, clientHeight, scrollHeight } = this._scrollContainer;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 2;
      if (isAtBottom) {
        const lastView = sections[sections.length - 1]?.view;
        if (lastView && lastView !== this._activeView()) {
          this._activeView.set(lastView);
        }
        return;
      }
    }

    const containerTop = this._scrollContainer?.getBoundingClientRect().top ?? 0;
    const headerHeight = this.workflowHeader()?.nativeElement.offsetHeight ?? 0;
    const activationLine = containerTop + headerHeight + 8;

    let nextActive = sections[0].view;
    for (const section of sections) {
      if (section.element.getBoundingClientRect().top <= activationLine) {
        nextActive = section.view;
      }
    }

    if (nextActive !== this._activeView()) {
      this._activeView.set(nextActive);
    }
  }
}
